<template>
  <div>
    <b-modal v-model="agreementModal" size="lg" hide-footer title="Subscription Terms">
      <div v-html="agreement" class="pb-3"></div>
    </b-modal>

    <div class="d-flex flex-column" v-if="selectedSubscription.plan">
      <div class="d-flex align-items-stretch flex-column flex-lg-row">
        <img :src="planImage(selectedSubscription.plan)" alt="plan logo" class="img-fluid"/>

        <div class="px-2 pt-1 d-flex flex-column flex-grow-1">

          <div class="d-flex flex-row flex-lg-column align-items-center align-items-lg-start py-2 py-lg-0">
            <div class="d-flex align-items-center">
              <div class="ic-photo"></div>
              <div class="ml-2 ic-camera" v-if="selectedSubscription.plan.identifier === 'all_access'"></div>
            </div>

            <h5 class="font-weight-bold m-0 mt-lg-2 ml-2 ml-lg-0">{{ selectedSubscription.plan.product_name }}</h5>
          </div>

          <p class="text-left flex-grow-1">{{ selectedSubscription.plan.description }}</p>

          <div class="d-flex" v-if="!publicPaymentCompleted">
            <onboarding-price-select-dropdown :prices="prices"
                                              :title="selectTitle"
                                              :selected="false"
                                              v-model="selectedPriceId"/>
          </div>
          <div class="d-flex justify-content-around" v-else>
            <h6 class="font-weight-semi-bold">Subscription</h6>
            <h6 class="font-weight-semi-bold">{{ selectedSubscription.priceName }}</h6>
            <h6 class="font-weight-semi-bold">{{ selectedSubscriptionIntervalLabel }}</h6>
          </div>
        </div>
      </div>

      <hr class="w-100"/>

      <div class="text-center" v-if="!publicPaymentCompleted">
        <h6 class="font-weight-semi-bold">Today your payment method will be charged</h6>

        <div class="text-dark d-flex align-items-center justify-content-center font-weight-semi-bold">
          <h3 class="m-0 mr-1 font-weight-semi-bold">
            ${{ amountPriceFormatted(selectedPriceId) }}</h3>
          <div> / {{ priceIntervalFormatted(selectedPriceId) }}</div>
        </div>
      </div>
    </div>

    <public-purchase-form
      v-if="!publicPaymentCompleted"
      :selected-subscription="selectedSubscription"
      :agreement="agreement"
      :code="code"
      product-type="subscription"
      @paymentCompleted="handlePaymentCompleted"
      @stripeDiscountChanged="handleStripeDiscountChanged"
      :button-title="buttonTitle"
    />

    <div class="d-flex flex-column align-items-center" v-else>
      <i class="far fa-3x fa-check-circle text-success m-2"></i>
      <h4 class="text-center font-weight-bold">Thank you for <br> your order</h4>
      <p class="text-gray-500 text-center">We've emailed you a receipt to <br> {{ email }}</p>

      <div class="py-3 text-center">
        <p class="font-weight-semi-bold mb-0">Check your email to create your account</p>
        <h4 class="font-weight-bold">{{ selectedSubscription.priceName }} Available</h4>
      </div>

      <p class="text-gray-500">You can Cancel Anytime</p>

      <a href="/search" class="btn btn-primary px-5 py-2 mb-4">Start Your Search</a>
    </div>

    <div class="text-center">
      <h6 class="font-weight-semi-bold">
        By purchasing a subscription, you agree to POCSTOCK's <br/>
        <a class="btn-link font-weight-semi-bold cursor-pointer" @click="agreementModal = true">Subscription Terms</a>,
        <a href="/terms" target="_blank">Terms of Service</a>
        and
        <a href="/legal/privacy" target="_blank">Privacy Policy</a>
      </h6>
    </div>

  </div>
</template>

<script>

import PublicPurchaseForm from "../Assets/PublicPurchaseForm.vue";
import GeneralMixins from "../../../mixins/GeneralMixins.js";
import AssetLoader from "../../../../AssetLoader.js";
import OnboardingPriceSelectDropdown from "./OnboardingPriceSelectDropdown.vue";

export default {
  components: {OnboardingPriceSelectDropdown, PublicPurchaseForm},
  mixins: [GeneralMixins],

  props: {
    value: {
      type: String,
      required: false,
      default: null
    },
    selectedSubscription: {
      type: Object,
      required: true
    },
    prices: {
      type: Array,
      required: true
    },
    selectTitle: {
      type: String,
      required: true
    },
    showPaymentModal: {
      type: Boolean,
      required: true
    },
    agreement: {
      type: String,
      required: true
    },
  },

  data() {
    return {
      code: null,
      agreementModal: false,
      publicPaymentCompleted: false,
      email: null,
      stripeDiscount: null,
    };
  },

  watch: {
    showPaymentModal(value) {
      if (!value) {
        this.stripeDiscount = null
      }
    }
  },

  methods: {
    getPrice(priceID) {
      return this.prices.find(p => p.id === priceID)
    },

    planImage(plan) {
      if (plan && plan.identifier) {
        return AssetLoader.imagePath(`${plan.identifier}.png`);
      }
    },

    amountPriceFormatted(priceID) {
      let amount = this.getPrice(priceID).amount

      if (this.stripeDiscount) {
        if (this.stripeDiscount.percent_off && this.stripeDiscount.percent_off > 0) {
          amount -= this.stripeDiscount.percent_off / 100 * amount
        } else if (this.stripeDiscount.amount_off && this.stripeDiscount.amount_off > 0) {
          amount -= this.stripeDiscount.amount_off
          if (amount < 0) {
            amount = 0
          }
        }
      }

      return this.formatPrice(amount / 100)
    },

    priceOptions(plan) {
      return plan.prices.map(price => {
        return {
          value: price.id,
          text: price.name
        }
      })
    },

    priceIntervalFormatted(priceID) {
      return `${this.getPrice(priceID).interval}`
    },

    handlePaymentCompleted(data) {
      this.email = data.email
      this.publicPaymentCompleted = true
    },

    handleStripeDiscountChanged(discount) {
      this.stripeDiscount = discount
    },
  },

  computed: {
    selectedPriceId: {
      get() {
        return this.value
      },
      set(selectedPriceId) {
        this.$emit('input', selectedPriceId)
      }
    },

    selectedSubscriptionIntervalLabel() {
      if (this.selectedSubscription.plan.identifier === "month") {
        return "Monthly"
      }
      return "Yearly"
    },

    buttonTitle() {
      if (!this.selectedSubscription.plan) {
        return "Select a Plan"
      }

      return `Start a ${this.selectedSubscription.plan.interval === 'month' ? 'Monthly' : 'Yearly'} Subscription`
    },
  },
}
</script>
